import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Home, Search, PlusCircle, User, Shield, Settings, Newspaper, Globe, Crown, HelpCircle, Menu, Bell, X, ArrowLeft, Mail } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import NotificationBell from './NotificationBell';
import InstallPrompt from './InstallPrompt';
import EmailVerificationBanner from './EmailVerificationBanner';
import PullToRefresh from './PullToRefresh';
import Footer from './Footer';
// import FeedbackController from './feedback/FeedbackController'; // popup uitgezet 28 sep 2026
import { HOI_BUUR_LOGO } from '../lib/constants';
import { SUPPORTED_LANGUAGES } from '../i18n/translations';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from './ui/dropdown-menu';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from './ui/sheet';
import { useCookieBannerVisible } from '../hooks/useCookieBannerVisible';

const Layout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, t, isAdmin, isSuperAdmin, language, isRTL, changeLanguage } = useAuth();
  const [moreOpen, setMoreOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  // Verberg de zwevende Hulp-knop terwijl de cookie-banner zichtbaar is.
  const cookieBannerVisible = useCookieBannerVisible();

  // Fetch unread notification count for mobile menu badge
  useEffect(() => {
    if (!user) { setUnreadCount(0); return; }
    const fetchUnread = async () => {
      try {
        const { default: api } = await import('../lib/api');
        const res = await api.get('/notifications/unread-count');
        setUnreadCount(res.data?.count || 0);
      } catch { /* ignore */ }
    };
    fetchUnread();
    const interval = setInterval(fetchUnread, 30000);
    return () => clearInterval(interval);
  }, [user]);

  // Core mobile nav: first 4 items + "Meer"
  const coreNavItems = [
    { path: '/', icon: Search, label: t('nav.discover'), exact: true },
    { path: '/nieuws', icon: Newspaper, label: t('nav.news') },
    { path: '/activiteit-toevoegen', icon: PlusCircle, label: t('nav.create'), mobileLabel: t('nav.create_short'), requiresAuth: true },
  ];

  // Desktop nav includes all
  const navItems = [
    ...coreNavItems,
    { path: '/profile', icon: User, label: t('nav.profile'), requiresAuth: true },
  ];

  if (isAdmin) {
    navItems.push({ path: '/admin', icon: Shield, label: t('nav.admin') });
  }
  
  if (isSuperAdmin) {
    navItems.push({ path: '/superadmin', icon: Crown, label: 'Super Admin' });
  }

  const isActive = (item) => {
    const path = typeof item === 'string' ? item : item.path;
    const exact = typeof item === 'object' && item.exact;
    if (path === '/' || exact) return location.pathname === path;
    return location.pathname.startsWith(path);
  };

  const handleNavClick = (item, e) => {
    // No more redirect to login - let pages handle auth themselves
  };

  // Don't show layout on auth pages
  const isAuthPage = ['/login', '/register', '/wachtwoord-vergeten'].includes(location.pathname);
  if (isAuthPage) {
    return <>{children}</>;
  }

  return (
    <div className="min-h-screen pb-24 md:pb-0 bg-[#FFF8F0] overflow-x-hidden">
      {/* Skip to content link - WCAG 2.1 AA */}
      <a href="#main-content" className="skip-to-content" data-testid="skip-to-content">
        Ga naar inhoud
      </a>
      {/* Desktop Top Navigation */}
      <header className="hidden md:block sticky top-0 bg-white/90 backdrop-blur-lg border-b border-hoi-blue/10 z-50" role="banner">
        <div className="max-w-5xl mx-auto px-6 h-16 flex items-center justify-center gap-6">
          <Link to="/" className="flex items-center flex-shrink-0" aria-label="Hoi Buur Almere - Home">
            <img src="/images/hoi-buur-logo.svg" alt="Hoi Buur" className="h-10 w-auto" />
          </Link>
          
          <nav className="flex items-center gap-1" aria-label={language === 'en' ? 'Main navigation' : 'Hoofdnavigatie'}>
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={(e) => handleNavClick(item, e)}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-full font-heading font-semibold text-xs transition-all duration-200 whitespace-nowrap ${
                  isActive(item)
                    ? 'bg-hoi-coral text-black'
                    : 'text-gray-700 hover:bg-hoi-peach/50'
                }`}
                aria-current={isActive(item) ? 'page' : undefined}
                data-testid={`nav-${item.path.replace('/', '') || 'home'}`}
              >
                <item.icon className="w-4 h-4" aria-hidden="true" />
                <span>{item.label}</span>
              </Link>
            ))}
            {/* Language switcher */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className="flex items-center gap-1 px-3 py-2 rounded-full text-sm font-semibold text-gray-600 hover:bg-hoi-peach/50 transition-all"
                  aria-label={t('auth.language_select')}
                  data-testid="language-switcher"
                >
                  <Globe className="w-4 h-4" aria-hidden="true" />
                  <span className="uppercase">{language}</span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align={isRTL ? "start" : "end"} className="rounded-xl">
                {SUPPORTED_LANGUAGES.map((lang) => (
                  <DropdownMenuItem
                    key={lang.code}
                    onClick={() => changeLanguage(lang.code)}
                    className={`cursor-pointer ${language === lang.code ? 'bg-hoi-peach/30 font-bold' : ''}`}
                    data-testid={`lang-option-${lang.code}`}
                  >
                    <span className={`font-mono ${isRTL ? 'ml-2' : 'mr-2'} text-xs`}>{lang.flag}</span>
                    {lang.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            {user && (
              <>
                <NotificationBell />
              </>
            )}
          </nav>
        </div>
      </header>

      {/* Mobile Top Bar with Notifications */}
      <header className="md:hidden sticky top-0 bg-white/90 backdrop-blur-lg border-b border-hoi-blue/10 z-50 px-4 py-2" role="banner">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {(() => {
              // Toon terug-knop op alle sub-pagina's. Verberg alleen op de
              // home-feed routes (/ + campagne-aliases) — daar is "terug" niet
              // betekenisvol.
              const homeRoutes = ['/', '/jongeren', '/senioren', '/volwassenen', '/gratis'];
              const isHome = homeRoutes.includes(location.pathname);
              if (isHome) return null;
              return (
                <button
                  type="button"
                  onClick={() => {
                    if (window.history.length > 1) navigate(-1);
                    else navigate('/');
                  }}
                  className="w-9 h-9 -ml-1 flex items-center justify-center rounded-full text-gray-700 hover:bg-hoi-peach/50 active:scale-95 transition-all"
                  aria-label="Terug"
                  data-testid="mobile-back-btn"
                >
                  <ArrowLeft className="w-5 h-5" aria-hidden="true" />
                </button>
              );
            })()}
            <Link to="/" className="flex items-center" aria-label="Hoi Buur Almere - Home">
              <img src="/images/hoi-buur-logo.svg" alt="Hoi Buur" className="h-10 w-auto" />
            </Link>
          </div>
          <div className="flex items-center gap-1">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className="px-2 py-1.5 min-h-[36px] min-w-[36px] flex items-center justify-center rounded-full text-xs font-bold text-gray-600 hover:bg-hoi-peach/50 transition-all border border-gray-200"
                  aria-label={t('auth.language_select')}
                  data-testid="mobile-language-switcher"
                >
                  {language.toUpperCase()}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align={isRTL ? "start" : "end"} className="rounded-xl">
                {SUPPORTED_LANGUAGES.map((lang) => (
                  <DropdownMenuItem
                    key={lang.code}
                    onClick={() => changeLanguage(lang.code)}
                    className={`cursor-pointer ${language === lang.code ? 'bg-hoi-peach/30 font-bold' : ''}`}
                    data-testid={`mobile-lang-option-${lang.code}`}
                  >
                    <span className={`font-mono ${isRTL ? 'ml-2' : 'mr-2'} text-xs`}>{lang.flag}</span>
                    {lang.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main
        id="main-content"
        className={`max-w-5xl mx-auto px-4 md:px-6 py-6 ${cookieBannerVisible ? 'pb-64 md:pb-6' : 'pb-24 md:pb-6'}`}
        role="main"
      >
        <EmailVerificationBanner />
        <PullToRefresh>
          {children}
        </PullToRefresh>
      </main>

      {/* Footer */}
      <Footer />

      {/* Mobile Bottom Navigation */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur-lg border-t border-hoi-blue/10 shadow-[0_-4px_20px_rgba(102,153,255,0.1)] z-[9999] safe-bottom" role="navigation" aria-label={language === 'en' ? 'Main navigation' : 'Hoofdnavigatie'}>
        <div className="flex items-center justify-around h-16 px-2">
          {coreNavItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              onClick={(e) => handleNavClick(item, e)}
              className={`flex flex-col items-center gap-1 px-3 py-2 rounded-2xl transition-all duration-200 active:scale-95 min-w-[3rem] ${
                isActive(item)
                  ? 'text-hoi-coral'
                  : 'text-gray-500'
              }`}
              aria-current={isActive(item) ? 'page' : undefined}
              data-testid={`mobile-nav-${item.path.replace('/', '') || 'home'}`}
            >
              <item.icon className={`w-6 h-6 ${isActive(item) ? 'stroke-[2.5px]' : ''}`} aria-hidden="true" />
              <span className="text-xs font-medium">{item.mobileLabel || item.label}</span>
            </Link>
          ))}
          {/* "Meer" button */}
          <button
            onClick={() => setMoreOpen(true)}
            className={`flex flex-col items-center gap-1 px-3 py-2 rounded-2xl transition-all duration-200 active:scale-95 min-w-[3rem] relative ${
              moreOpen ? 'text-hoi-coral' : 'text-gray-500'
            }`}
            aria-label="Meer opties"
            data-testid="mobile-nav-more"
          >
            <div className="relative">
              <Menu className="w-6 h-6" aria-hidden="true" />
              {unreadCount > 0 && (
                <span className="absolute -top-1.5 -right-2.5 bg-hoi-coral text-white text-[10px] font-bold min-w-[16px] h-4 flex items-center justify-center rounded-full px-1">
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
            </div>
            <span className="text-xs font-medium">Meer</span>
          </button>
        </div>
      </nav>

      {/* Mobile "Meer" Sheet */}
      <Sheet open={moreOpen} onOpenChange={setMoreOpen}>
        <SheetContent side="bottom" className="rounded-t-2xl px-4 pb-24 max-h-[85vh] overflow-y-auto">
          <SheetHeader className="pb-3 border-b">
            <SheetTitle className="font-heading text-lg">Menu</SheetTitle>
            <SheetDescription className="sr-only">Extra navigatie-opties</SheetDescription>
          </SheetHeader>
          <div className="py-3 space-y-1">
            {/* Profile */}
            <Link
              to={user ? '/profile' : '/login'}
              onClick={() => setMoreOpen(false)}
              className={`flex items-center gap-3 px-4 py-3 transition-colors ${isActive('/profile') ? 'bg-hoi-coral/10 text-hoi-coral' : 'hover:bg-gray-50'}`}
              data-testid="more-menu-profile"
            >
              <User className="w-5 h-5" />
              <span className="font-medium">{t('nav.profile')}</span>
            </Link>

            {/* Notifications — always visible */}
            <Link
              to={user ? '/meldingen' : '/login'}
              onClick={() => setMoreOpen(false)}
              className={`flex items-center gap-3 px-4 py-3 transition-colors ${isActive('/meldingen') ? 'bg-hoi-coral/10 text-hoi-coral' : 'hover:bg-gray-50'}`}
              data-testid="more-menu-notifications"
            >
              <div className="relative">
                <Bell className="w-5 h-5" />
                {unreadCount > 0 && (
                  <span className="absolute -top-1.5 -right-1.5 bg-hoi-coral text-white text-[10px] font-bold min-w-[16px] h-4 flex items-center justify-center rounded-full px-1">
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </span>
                )}
              </div>
              <span className="font-medium">Meldingen</span>
              {unreadCount > 0 && (
                <span className="ml-auto text-xs bg-hoi-coral/10 text-hoi-coral px-2 py-0.5 font-semibold">
                  {unreadCount} nieuw
                </span>
              )}
            </Link>

            {/* Admin */}
            {isAdmin && (
              <Link
                to="/admin"
                onClick={() => setMoreOpen(false)}
                className={`flex items-center gap-3 px-4 py-3 transition-colors ${isActive('/admin') ? 'bg-hoi-coral/10 text-hoi-coral' : 'hover:bg-gray-50'}`}
                data-testid="more-menu-admin"
              >
                <Shield className="w-5 h-5" />
                <span className="font-medium">{t('nav.admin')}</span>
              </Link>
            )}

            {/* Super Admin */}
            {isSuperAdmin && (
              <Link
                to="/superadmin"
                onClick={() => setMoreOpen(false)}
                className={`flex items-center gap-3 px-4 py-3 transition-colors ${isActive('/superadmin') ? 'bg-hoi-coral/10 text-hoi-coral' : 'hover:bg-gray-50'}`}
                data-testid="more-menu-superadmin"
              >
                <Crown className="w-5 h-5" />
                <span className="font-medium">Super Admin</span>
              </Link>
            )}

            {/* Settings */}
            <Link
              to={user ? '/settings' : '/login'}
              onClick={() => setMoreOpen(false)}
              className={`flex items-center gap-3 px-4 py-3 transition-colors ${isActive('/settings') ? 'bg-hoi-coral/10 text-hoi-coral' : 'hover:bg-gray-50'}`}
              data-testid="more-menu-settings"
            >
              <Settings className="w-5 h-5" />
              <span className="font-medium">{t('common.settings')}</span>
            </Link>

            {/* Contact — direct bereikbaar vanuit mobiele menu (fix iPhone vindbaarheid) */}
            <Link
              to="/contact"
              onClick={() => setMoreOpen(false)}
              className={`flex items-center gap-3 px-4 py-3 transition-colors ${isActive('/contact') ? 'bg-hoi-coral/10 text-hoi-coral' : 'hover:bg-gray-50'}`}
              data-testid="more-menu-contact"
            >
              <Mail className="w-5 h-5" />
              <span className="font-medium">Contact</span>
            </Link>

            {/* Language — always visible */}
            <div className="px-4 py-3 border-t mt-2">
              <p className="text-xs text-muted-foreground mb-2 font-medium uppercase tracking-wider">Taal</p>
              <div className="flex flex-wrap gap-2">
                {SUPPORTED_LANGUAGES.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => { changeLanguage(lang.code); setMoreOpen(false); }}
                    className={`px-3 py-2 text-sm font-medium border transition-colors ${
                      language === lang.code
                        ? 'bg-hoi-blue text-white border-hoi-blue'
                        : 'bg-white text-gray-700 border-gray-200 hover:border-hoi-blue/50'
                    }`}
                    data-testid={`more-lang-${lang.code}`}
                  >
                    {lang.flag} {lang.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      {/* Floating Help Button */}
      <Link
        to="/hulp"
        className={`fixed bottom-24 right-4 md:bottom-6 md:right-6 z-[9998] bg-brand text-white w-10 h-10 flex items-center justify-center shadow-lg hover:shadow-xl transition-all hover:scale-105 active:scale-95 rounded-full ${
          cookieBannerVisible ? 'opacity-0 pointer-events-none' : 'opacity-100'
        }`}
        aria-label="Hulp nodig?"
        aria-hidden={cookieBannerVisible ? 'true' : undefined}
        tabIndex={cookieBannerVisible ? -1 : 0}
        data-testid="floating-help-btn"
      >
        <HelpCircle className="w-6 h-6" />
      </Link>

      {/* PWA Install Prompt */}
      <InstallPrompt />

      {/* Feedback pop-up UITGEZET op verzoek (28 sep 2026). Statistieken-sectie
          en SuperAdmin blijven ongewijzigd en tonen de reeds verzamelde data.
          Terugzetten: onderstaande regel weer activeren. */}
      {/* <FeedbackController /> */}
    </div>
  );
};

export default Layout;
